import moment from 'moment-timezone';
import React, { useState, useEffect } from 'react';
import { FormGroup, InputGroup } from '@blueprintjs/core';
import { Markup, MarkupProps, useField } from 'blueprint-form';
import { Row, Col } from '../../../grid';

const DOB_FORMAT = 'YYYY-MM-DD';
const REGEX_YEAR = /^(19|20)[0-9]{2}$/;

const DOBInput = ({ label, name }: MarkupProps): JSX.Element => {
  const id = Markup.getID();
  const [field, meta, helpers] = useField<string>({
    name: name,
    validate: (value: string) => {
      let error: string = null;
      const time = moment(value, DOB_FORMAT, true);
      if (!time.isValid() && value.replace(/\D+/g, '') !== '') {
        error = 'The date of birth format is invalid!';
      }
      return error;
    }
  });
  let time: moment.Moment = null;
  if (field.value) {
    const _time = moment(field.value, DOB_FORMAT, true);
    if (_time.isValid()) {
      time = _time;
    }
  }
  const [month, setMonth] = useState<string>(time?.format('MM') || '');
  const [isMonthTouched, setIsMonthTouched] = useState<boolean>(false);
  const [day, setDay] = useState<string>(time?.format('DD') || '');
  const [isDayTouched, setIsDayTouched] = useState<boolean>(false);
  const [year, setYear] = useState<string>(time?.format('YYYY') || '');
  const [isYearTouched, setIsYearTouched] = useState<boolean>(false);
  // Listen for changes
  const onValueChange = (
    field: 'day' | 'month' | 'year',
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.currentTarget.value;
    if (field === 'day') {
      setDay(value);
    } else if (field === 'month') {
      setMonth(value);
    } else if (field === 'year') {
      setYear(value);
    }
  };
  const onMonthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const month = parseInt(event.currentTarget.value, 10);
    if (!(1 <= month && month <= 12)) {
      setMonth('');
      setIsMonthTouched(true);
      return;
    }
    setMonth(month < 10 ? `0${month}` : String(month));
    setIsMonthTouched(true);
  };
  const onDayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const day = parseInt(event.currentTarget.value, 10);
    if (!(1 <= day && day <= 31)) {
      setDay('');
      setIsDayTouched(true);
      return;
    }
    setDay(day < 10 ? `0${day}` : String(day));
    setIsDayTouched(true);
  };
  const onYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const year = event.currentTarget.value.trim();
    if (!REGEX_YEAR.test(year)) {
      setYear('');
      setIsYearTouched(true);
      return;
    }
    setYear(year);
    setIsYearTouched(true);
  };
  // Set the field value if all components are valid
  useEffect(() => {
    if (year.trim() === '' && month.trim() === '' && day.trim() === '') {
      helpers.setValue('');
      return;
    }
    const dob = `${year}-${month}-${day}`;
    const time = moment(dob, DOB_FORMAT, true);
    if (time.isValid()) {
      helpers.setValue(time.format(DOB_FORMAT));
    }
    helpers.setValue(dob);
    return;
  }, [day, month, year]);
  useEffect(() => {
    if (isDayTouched && isMonthTouched && isYearTouched) {
      helpers.setTouched(true);
    }
    return;
  }, [isDayTouched, isMonthTouched, isYearTouched]);
  return (
    <FormGroup
      label={label}
      labelFor={id}
      intent={meta.touched && meta.error ? 'danger' : 'none'}
      helperText={meta.touched ? meta.error : null}
    >
      <Row>
        <Col xs={6}>
          <InputGroup
            style={{
              textAlign: 'center'
            }}
            intent={meta.touched && meta.error ? 'danger' : 'none'}
            placeholder="MM"
            aria-label="Birthdate Month"
            value={month}
            onChange={onValueChange.bind(this, 'month')}
            onBlur={onMonthChange}
          />
        </Col>
        <Col xs={6}>
          <InputGroup
            style={{
              textAlign: 'center'
            }}
            intent={meta.touched && meta.error ? 'danger' : 'none'}
            placeholder="DD"
            aria-label="Birthdate Day"
            value={day}
            onChange={onValueChange.bind(this, 'day')}
            onBlur={onDayChange}
          />
        </Col>
        <Col xs={12}>
          <InputGroup
            style={{
              textAlign: 'center'
            }}
            intent={meta.touched && meta.error ? 'danger' : 'none'}
            placeholder="YYYY"
            aria-label="Birthdate Year"
            value={year}
            onChange={onValueChange.bind(this, 'year')}
            onBlur={onYearChange}
          />
        </Col>
      </Row>
      <input id={id} type="hidden" name={name} value={field.value} />
    </FormGroup>
  );
};

export default DOBInput;
